import type { CallAudit } from "~/features/call-audit/types";
import { formatDateTimeFromISO, formatDateWithYear, formatWindowLabel, orDoubleDash, stripCountryCode } from "~/shared/utils/formatting";
import { OutcomeBadge, getOutcomeLabel } from "~/features/orders/components/OutcomeBadge";
import { InlineFilterDropdown } from "~/shared/components/InlineFilterDropdown";
import { GlobalSearchBar } from "~/shared/components/GlobalSearchBar";
import React, { useMemo } from "react";

type CallAuditTableProps = Readonly<{
  calls: CallAudit[];
  loading?: boolean;
  emptyMessage: string;
  scrollRef?: React.RefObject<HTMLDivElement>;
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  onSearchSubmit?: (value: string) => void;
  cityFilter?: string[];
  onCityFilterChange?: (selected: string[]) => void;
  cityOptions?: string[];
  outcomeFilter?: string[];
  onOutcomeFilterChange?: (selected: string[]) => void;
  whiteGloveFilter?: string[];
  onWhiteGloveFilterChange?: (selected: string[]) => void;
}>;

export function CallAuditTable({
  calls,
  loading = false,
  emptyMessage,
  scrollRef,
  searchQuery = "",
  onSearchChange,
  onSearchSubmit,
  cityFilter = [],
  onCityFilterChange,
  cityOptions,
  outcomeFilter = [],
  onOutcomeFilterChange,
  whiteGloveFilter = [],
  onWhiteGloveFilterChange,
}: CallAuditTableProps) {
  const [openNotes, setOpenNotes] = React.useState<string | null>(null);
  const [openComments, setOpenComments] = React.useState<string | null>(null);
  const notesDialogRef = React.useRef<HTMLDialogElement | null>(null);
  const commentsDialogRef = React.useRef<HTMLDialogElement | null>(null);

  // Apply client-side filters; options derived from all calls so dropdowns stay populated
  const displayedCalls = useMemo(() => {
    let result = calls;
    if (outcomeFilter.length > 0)
      result = result.filter(c => c.outcome && outcomeFilter.includes(c.outcome));
    if (whiteGloveFilter.length > 0)
      result = result.filter(c => whiteGloveFilter.includes(c.whiteGlove ? 'Yes' : 'No'));
    return result;
  }, [calls, outcomeFilter, whiteGloveFilter]);

  const isEmpty = !loading && displayedCalls.length === 0;
  const hasActiveFilter = cityFilter.length > 0 || outcomeFilter.length > 0 || whiteGloveFilter.length > 0;

  React.useEffect(() => {
    const dialog = notesDialogRef.current;
    if (!dialog) return;
    if (openNotes !== null) {
      if (!dialog.open) dialog.showModal();
      const onBackdropClick = (e: MouseEvent) => {
        if (e.target === dialog) setOpenNotes(null);
      };
      dialog.addEventListener("click", onBackdropClick);
      return () => dialog.removeEventListener("click", onBackdropClick);
    }
    if (dialog.open) dialog.close();
  }, [openNotes]);

  React.useEffect(() => {
    const dialog = commentsDialogRef.current;
    if (!dialog) return;
    if (openComments !== null) {
      if (!dialog.open) dialog.showModal();
      const onBackdropClick = (e: MouseEvent) => {
        if (e.target === dialog) setOpenComments(null);
      };
      dialog.addEventListener("click", onBackdropClick);
      return () => dialog.removeEventListener("click", onBackdropClick);
    }
    if (dialog.open) dialog.close();
  }, [openComments]);

  // Extract unique filter options from all calls (unfiltered) so dropdowns stay populated
  const filterOptions = useMemo(() => {
    const outcomes = new Set<string>();
    calls.forEach(c => {
      if (c.outcome) outcomes.add(c.outcome);
    });
    const alpha = (a: string, b: string) => a.localeCompare(b);
    return {
      cities: cityOptions && cityOptions.length > 0 ? cityOptions : calls.map(c => c.city).filter(Boolean).sort(alpha),
      outcomes: Array.from(outcomes).sort(alpha),
      whiteGloves: ['Yes', 'No'],
    };
  }, [calls, cityOptions]);

  return (
    <div
      className="table-container table-container--scroll-inner"
      style={{ display: 'flex', flexDirection: 'column', flex: isEmpty ? '0 0 auto' : 1, minHeight: 0, position: 'relative' }}
    >
      {loading && calls.length > 0 && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(255,255,255,0.7)',
            zIndex: 20,
          }}
        >
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
        </div>
      )}
      {/* Notes popup — native <dialog> for proper accessibility & focus trapping */}
      <dialog
        ref={notesDialogRef}
        className="popup-dialog"
        aria-label="Notes"
        onCancel={(e) => { e.preventDefault(); setOpenNotes(null); }}
      >
        <div
          style={{
            background: "white",
            borderRadius: 10,
            border: "1px solid var(--border)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            maxHeight: "min(70vh, 520px)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 14px",
              borderBottom: "1px solid var(--border)",
              background: "var(--neutral-gray)",
            }}
          >
            <strong style={{ fontSize: 14 }}>Notes</strong>
            <button
              type="button"
              onClick={() => setOpenNotes(null)}
              aria-label="Close"
              style={{
                background: "white",
                border: "1px solid var(--border)",
                borderRadius: "50%",
                width: 28,
                height: 28,
                fontSize: 18,
                cursor: "pointer",
                color: "var(--text-muted)",
                padding: 0,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>
          <div style={{ padding: 14, overflow: "auto" }}>
            <div
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: 14,
                lineHeight: 1.4,
                color: "var(--text)",
              }}
            >
              {openNotes}
            </div>
          </div>
        </div>
      </dialog>

      {/* Comments popup — native <dialog> for proper accessibility & focus trapping */}
      <dialog
        ref={commentsDialogRef}
        className="popup-dialog"
        aria-label="Comments"
        onCancel={(e) => { e.preventDefault(); setOpenComments(null); }}
      >
        <div
          style={{
            background: "white",
            borderRadius: 10,
            border: "1px solid var(--border)",
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            maxHeight: "min(70vh, 520px)",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 14px",
              borderBottom: "1px solid var(--border)",
              background: "var(--neutral-gray)",
            }}
          >
            <strong style={{ fontSize: 14 }}>Comments</strong>
            <button
              type="button"
              onClick={() => setOpenComments(null)}
              aria-label="Close"
              style={{
                background: "white",
                border: "1px solid var(--border)",
                borderRadius: "50%",
                width: 28,
                height: 28,
                fontSize: 18,
                cursor: "pointer",
                color: "var(--text-muted)",
                padding: 0,
                lineHeight: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              ×
            </button>
          </div>
          <div style={{ padding: 14, overflow: "auto" }}>
            <div
              style={{
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: 14,
                lineHeight: 1.4,
                color: "var(--text)",
              }}
            >
              {openComments}
            </div>
          </div>
        </div>
      </dialog>
      <div
        style={{
          padding: "16px",
          borderBottom: "1px solid var(--border)",
          background: "var(--neutral-gray)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexShrink: 0,
        }}
      >
        <h3 style={{ margin: 0, fontSize: "16px", fontWeight: 600 }}>Recent Calls</h3>
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 24,
            height: 24,
            borderRadius: "50%",
            background: "var(--primary-blue)",
            color: "white",
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {displayedCalls.length}
        </span>
        <div style={{ flex: 1 }} />
        <GlobalSearchBar
          value={searchQuery}
          onChange={onSearchChange ?? (() => { })}
          onSubmit={onSearchSubmit ?? (() => { })}
          placeholder="Search by SM#, patient, patient ID, city, postal, phone…"
        />
      </div>
      <div
        ref={scrollRef}
        className="table-scroll-wrapper"
        style={{
          flex: isEmpty ? '0 0 auto' : 1,
          overflowY: isEmpty ? 'hidden' : 'auto',
          height: isEmpty ? 170 : undefined,
          minHeight: isEmpty ? 170 : 0,
          position: 'relative',
        }}
      >
        {!loading && isEmpty && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              padding: '16px 24px',
              color: 'var(--text-muted)',
              pointerEvents: 'none',
            }}
          >
            <div style={{ fontWeight: 600, fontSize: 16, color: 'var(--text-muted)' }}>
              {hasActiveFilter ? 'No calls match the selected filters' : emptyMessage}
            </div>
          </div>
        )}
        <table className="table">
          <thead>
            <tr>
              <th>When</th>
              <th>SM Number</th>
              <th>Patient</th>
              <th>Patient ID</th>
              <th>
                <InlineFilterDropdown
                  label="White Glove"
                  options={filterOptions.whiteGloves}
                  selected={whiteGloveFilter}
                  onChange={onWhiteGloveFilterChange ?? (() => { })}
                />
              </th>
              <th>
                <InlineFilterDropdown
                  label="Outcome"
                  options={filterOptions.outcomes}
                  selected={outcomeFilter}
                  onChange={onOutcomeFilterChange ?? (() => { })}
                  formatOption={getOutcomeLabel}
                />
              </th>
              <th>Window</th>
              <th>New Date</th>
              <th>
                <InlineFilterDropdown
                  label="City"
                  options={filterOptions.cities}
                  selected={cityFilter}
                  onChange={onCityFilterChange ?? (() => { })}
                  searchable
                />
              </th>
              <th>Postal</th>
              <th>Phone</th>
              <th>Alt Phone</th>
              <th>Agent</th>
              <th>Needs Callback?</th>
              <th>Comments</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {displayedCalls.map((c) => {
              const createdAtDateTime = c.createdAt ? formatDateTimeFromISO(c.createdAt) : null;
              const notesText = (c.notes ?? "").trim();
              const notesPreview = notesText.length > 40 ? `${notesText.slice(0, 40)}…` : notesText;
              const commentsText = (c.comments ?? "").trim();
              const commentsPreview = commentsText.length > 40 ? `${commentsText.slice(0, 40)}…` : commentsText;
              return (
                <tr key={c._id}>
                  <td>{createdAtDateTime ? `${createdAtDateTime.date} ${createdAtDateTime.time}` : orDoubleDash(null)}</td>
                  <td>
                    <strong>{c.orderId}</strong>
                  </td>
                  <td>{orDoubleDash(c.patientName)}</td>
                  <td>{orDoubleDash(c.patientId)}</td>
                  <td>{c.whiteGlove ? "Yes" : "No"}</td>
                  <td>{c.outcome ? <OutcomeBadge outcome={c.outcome} /> : orDoubleDash(c.outcome)}</td>
                  <td>{orDoubleDash(formatWindowLabel(c.window))}</td>
                  <td>{c.deliveryDate ? formatDateWithYear(c.deliveryDate) : orDoubleDash(null)}</td>
                  <td>{orDoubleDash(c.city)}</td>
                  <td>{orDoubleDash(c.postalCode)}</td>
                  <td>{orDoubleDash(stripCountryCode(c.phone))}</td>
                  <td>{orDoubleDash(stripCountryCode(c.altPhone))}</td>
                  <td>{orDoubleDash(c.agent)}</td>
                  <td>{c.needCallback ? <span className="badge warning">Yes</span> : <span className="badge success">No</span>}</td>
                  <td>
                    {commentsText ? (
                      <button
                        type="button"
                        onClick={() => setOpenComments(commentsText)}
                        title="Click to view full comments"
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "left",
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          margin: 0,
                          cursor: "pointer",
                          color: "inherit",
                        }}
                      >
                        <span
                          style={{
                            display: "block",
                            maxWidth: 260,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {commentsPreview}
                        </span>
                      </button>
                    ) : (
                      orDoubleDash(null)
                    )}
                  </td>
                  <td>
                    {notesText ? (
                      <button
                        type="button"
                        onClick={() => setOpenNotes(notesText)}
                        title="Click to view full notes"
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "left",
                          background: "transparent",
                          border: "none",
                          padding: 0,
                          margin: 0,
                          cursor: "pointer",
                          color: "inherit",
                        }}
                      >
                        <span
                          style={{
                            display: "block",
                            maxWidth: 260,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {notesPreview}
                        </span>
                      </button>
                    ) : (
                      orDoubleDash(null)
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
